/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Constants
import { queryKeys } from '@config/constants/query-keys';

// Types
import { TApiError } from '@global-types/api/api';
import { TApiResponseDeleteBrand } from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const remove = (
	id: string
): Promise<TApiResponseDeleteBrand> => {
	return apiClient.delete(`/organizations/${id} `);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useRemove = (page: number, size: number) => {
	const { t } = useTranslation();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'organizations.notifications.organizationSuccessfullyDeleted'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.organizations.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});
};
